// src/pages/QRCompletePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type QRCompleteItem = {
  id: number;
  productCode: string;
  productName: string;
  qty: number;
  imageUrl?: string;
};

type QRCompleteUSAItem = {
  id: number;
  productCode: string;
  productName: string;
  qty: number;
  transferNo: string;
  imageUrl?: string;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

type AllItem = {
  id: number;
  productCode: string;
  productName: string;
  qty: number;
  source: "shop" | "usa";
  transferNo?: string;
  imageUrl?: string;
};

export default function QRCompletePage(): JSX.Element {
  const [tab, setTab] = useState<"shop" | "usa" | "all">("shop");

  const [items, setItems] = useState<QRCompleteItem[]>([]);
  const [usaItems, setUsaItems] = useState<QRCompleteUSAItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [pending, setPending] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  // PMN-CRITICAL(/QRCompletePage): SHOP list is backed by /qr/complete, which returns the latest untagged purchase movement per product, not every untagged lot.
  const fetchShop = async (query?: string) => {
    try {
      setLoading(true);
      setErrorMsg("");
      const url = query
        ? apiUrl(`/qr/complete?q=${encodeURIComponent(query)}`)
        : apiUrl("/qr/complete");
      const resp = await fetch(url);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);
      const list: QRCompleteItem[] = (json.items ?? []).map((r: any) => ({
        id: Number(r.id),
        productCode: String(r.productCode ?? r.product_code ?? ""),
        productName: String(r.productName ?? r.product_name ?? ""),
        qty: Number(r.qty ?? 0),
        imageUrl: r.imageUrl ?? r.url_images ?? undefined,
      }));
      setItems(list);
    } catch (err: any) {
      const msg = err?.message ?? "โหลดรายการไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  // PMN-CRITICAL(/QRCompletePage): USA list is backed by stock_transfer destination lots at location_id=4 with qr_tagged=0.
  const fetchUSA = async (query?: string) => {
    try {
      setLoading(true);
      setErrorMsg("");
      const url = query
        ? apiUrl(`/qr/complete-usa?q=${encodeURIComponent(query)}`)
        : apiUrl("/qr/complete-usa");
      const resp = await fetch(url);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);
      const list: QRCompleteUSAItem[] = (json.items ?? []).map((r: any) => ({
        id: Number(r.id),
        productCode: String(r.productCode ?? r.product_code ?? ""),
        productName: String(r.productName ?? r.product_name ?? ""),
        qty: Number(r.qty ?? 0),
        transferNo: String(r.transferNo ?? ""),
        imageUrl: r.imageUrl ?? r.url_images ?? undefined,
      }));
      setUsaItems(list);
    } catch (err: any) {
      const msg = err?.message ?? "โหลดรายการไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setQ("");
    setQInput("");
    setErrorMsg("");
    if (tab === "shop") fetchShop();
    else if (tab === "usa") fetchUSA();
    else { fetchShop(); fetchUSA(); }
  }, [tab]);

  const doSearch = () => {
    const query = qInput.trim();
    setQ(query);
    if (tab === "shop") fetchShop(query || undefined);
    else if (tab === "usa") fetchUSA(query || undefined);
    else { fetchShop(query || undefined); fetchUSA(query || undefined); }
  };

  const refresh = () => {
    if (tab === "shop") fetchShop(q || undefined);
    else if (tab === "usa") fetchUSA(q || undefined);
    else { fetchShop(q || undefined); fetchUSA(q || undefined); }
  };

  // PMN-CRITICAL(/QRCompletePage): Marking updates inventory_movements.qr_tagged for the given movement id; remove the row from the correct source list only after PATCH success.
  const markTagged = async (id: number, source?: "shop" | "usa") => {
    try {
      setPending((prev) => new Set(prev).add(id));
      const resp = await fetch(apiUrl(`/qr/mark/${id}`), { method: "PATCH" });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

      const src = source ?? tab;
      if (src === "shop") {
        setItems((prev) => prev.filter((x) => x.id !== id));
      } else if (src === "usa") {
        setUsaItems((prev) => prev.filter((x) => x.id !== id));
      }
      setToast({ show: true, message: `อัปเดต #${id} เรียบร้อย`, type: "success" });
    } catch (err: any) {
      setToast({ show: true, message: err?.message ?? "อัปเดตไม่สำเร็จ", type: "danger" });
    } finally {
      setPending((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const filteredShop = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    if (!keyword) return items;
    return items.filter(
      (p) =>
        p.productCode.toLowerCase().includes(keyword) ||
        p.productName.toLowerCase().includes(keyword)
    );
  }, [items, q]);

  const filteredUSA = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    if (!keyword) return usaItems;
    return usaItems.filter(
      (p) =>
        p.productCode.toLowerCase().includes(keyword) ||
        p.productName.toLowerCase().includes(keyword) ||
        p.transferNo.toLowerCase().includes(keyword)
    );
  }, [usaItems, q]);

  const filteredAll = useMemo<AllItem[]>(() => {
    const keyword = q.trim().toLowerCase();
    const shopMapped: AllItem[] = items.map((p) => ({
      id: p.id, productCode: p.productCode, productName: p.productName,
      qty: p.qty, source: "shop", imageUrl: p.imageUrl,
    }));
    const usaMapped: AllItem[] = usaItems.map((p) => ({
      id: p.id, productCode: p.productCode, productName: p.productName,
      qty: p.qty, source: "usa", transferNo: p.transferNo, imageUrl: p.imageUrl,
    }));
    const combined = [...shopMapped, ...usaMapped];
    if (!keyword) return combined;
    return combined.filter(
      (p) =>
        p.productCode.toLowerCase().includes(keyword) ||
        p.productName.toLowerCase().includes(keyword) ||
        (p.transferNo ?? "").toLowerCase().includes(keyword)
    );
  }, [items, usaItems, q]);

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">รายการแปะ QR เสร็จสิ้น</h4>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh}>
                      รีเฟรช
                    </button>
                  </div>
                </div>

                {/* Tab Toggle */}
                <div className="btn-group mb-3 w-100" role="group">
                  <button
                    type="button"
                    className={`btn ${tab === "all" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => setTab("all")}
                  >
                    ทั้งหมด
                  </button>
                  <button
                    type="button"
                    className={`btn ${tab === "shop" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setTab("shop")}
                  >
                    ร้าน (SHOP)
                  </button>
                  <button
                    type="button"
                    className={`btn ${tab === "usa" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setTab("usa")}
                  >
                    USA
                  </button>
                </div>

                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด/ชื่อ (กด Enter เพื่อค้นหา server)"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={doSearch}>
                    ค้นหา
                  </button>
                </div>

                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการ…</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    {errorMsg}
                  </div>
                )}

                {/* Shop Table */}
                {tab === "shop" && (
                  <div className="border rounded-3 p-0 bg-white">
                    <div className="table-responsive" style={{ maxHeight: 520 }}>
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: 76 }}>รูป</th>
                            <th style={{ width: 160 }}>โค้ด</th>
                            <th>ชื่อสินค้า</th>
                            <th style={{ width: 110 }} className="text-end">จำนวน</th>
                            <th style={{ width: 120 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredShop.length === 0 && !loading && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-4">
                                ไม่พบรายการ
                              </td>
                            </tr>
                          )}
                          {filteredShop.map((p) => (
                            <tr key={p.id} className="user-select-none">
                              <td>
                                {p.imageUrl ? (
                                  <img
                                    src={p.imageUrl}
                                    alt={p.productName}
                                    width={56}
                                    height={56}
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(p.imageUrl ?? null)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                )}
                              </td>
                              <td className="fw-semibold">{p.productCode}</td>
                              <td>{p.productName}</td>
                              <td className="text-end">{p.qty}</td>
                              <td className="text-end">
                                <button
                                  className="btn btn-success btn-sm"
                                  disabled={pending.has(p.id)}
                                  onClick={() => markTagged(p.id)}
                                  title={`ติดแล้ว (id: ${p.id})`}
                                >
                                  {pending.has(p.id) ? "กำลังบันทึก..." : "ติดแล้ว"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* USA Table */}
                {tab === "usa" && (
                  <div className="border rounded-3 p-0 bg-white">
                    <div className="table-responsive" style={{ maxHeight: 480 }}>
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: 76 }}>รูป</th>
                            <th style={{ width: 140 }}>โค้ด</th>
                            <th>ชื่อสินค้า</th>
                            <th style={{ width: 110 }} className="text-end">จำนวน</th>
                            <th style={{ width: 170 }}>Transfer No.</th>
                            <th style={{ width: 120 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUSA.length === 0 && !loading && (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">
                                ไม่พบรายการ
                              </td>
                            </tr>
                          )}
                          {filteredUSA.map((p) => (
                            <tr key={p.id} className="user-select-none">
                              <td>
                                {p.imageUrl ? (
                                  <img
                                    src={p.imageUrl}
                                    alt={p.productName}
                                    width={56}
                                    height={56}
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(p.imageUrl ?? null)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                )}
                              </td>
                              <td className="fw-semibold">{p.productCode}</td>
                              <td>{p.productName}</td>
                              <td className="text-end">{p.qty}</td>
                              <td className="small text-muted">{p.transferNo}</td>
                              <td className="text-end">
                                <button
                                  className="btn btn-success btn-sm"
                                  disabled={pending.has(p.id)}
                                  onClick={() => markTagged(p.id)}
                                  title={`ติดแล้ว (id: ${p.id})`}
                                >
                                  {pending.has(p.id) ? "กำลังบันทึก..." : "ติดแล้ว"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-light rounded-bottom-3">
                      <span className="text-muted small me-2">รวมทั้งหมด</span>
                      <span className="fw-bold fs-6">
                        {filteredUSA.reduce((sum, p) => sum + p.qty, 0).toLocaleString()} ชิ้น
                      </span>
                      <span className="text-muted small ms-3">({filteredUSA.length} รายการ)</span>
                    </div>
                  </div>
                )}

                {/* All Table */}
                {tab === "all" && (
                  <div className="border rounded-3 p-0 bg-white">
                    <div className="table-responsive" style={{ maxHeight: 520 }}>
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: 76 }}>รูป</th>
                            <th style={{ width: 140 }}>โค้ด</th>
                            <th>ชื่อสินค้า</th>
                            <th style={{ width: 80 }} className="text-center">แหล่ง</th>
                            <th style={{ width: 90 }} className="text-end">จำนวน</th>
                            <th style={{ width: 120 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAll.length === 0 && !loading && (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">
                                ไม่พบรายการ
                              </td>
                            </tr>
                          )}
                          {filteredAll.map((p) => (
                            <tr key={`${p.source}-${p.id}`} className="user-select-none">
                              <td>
                                {p.imageUrl ? (
                                  <img
                                    src={p.imageUrl}
                                    alt={p.productName}
                                    width={56}
                                    height={56}
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(p.imageUrl ?? null)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                )}
                              </td>
                              <td className="fw-semibold">{p.productCode}</td>
                              <td>
                                <div>{p.productName}</div>
                                {p.transferNo && (
                                  <div className="small text-muted">{p.transferNo}</div>
                                )}
                              </td>
                              <td className="text-center">
                                <span className={`badge ${p.source === "shop" ? "bg-primary" : "bg-danger"}`}>
                                  {p.source === "shop" ? "SHOP" : "USA"}
                                </span>
                              </td>
                              <td className="text-end">{p.qty}</td>
                              <td className="text-end">
                                <button
                                  className="btn btn-success btn-sm"
                                  disabled={pending.has(p.id)}
                                  onClick={() => markTagged(p.id, p.source)}
                                  title={`ติดแล้ว (id: ${p.id})`}
                                >
                                  {pending.has(p.id) ? "กำลังบันทึก..." : "ติดแล้ว"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-light rounded-bottom-3">
                      <span className="text-muted small me-2">รวมทั้งหมด</span>
                      <span className="fw-bold fs-6">
                        {filteredAll.reduce((sum, p) => sum + p.qty, 0).toLocaleString()} ชิ้น
                      </span>
                      <span className="text-muted small ms-3">({filteredAll.length} รายการ)</span>
                    </div>
                  </div>
                )}

                <div className="text-center small text-muted mt-3">
                  {tab === "shop"
                    ? <span>* ดึงจาก <code>/qr/complete</code> เฉพาะ "แถวล่าสุด/สินค้าหนึ่งตัว" ที่ยังไม่ได้ติด QR</span>
                    : tab === "usa"
                    ? <span>* ดึงจาก <code>/qr/complete-usa</code> สินค้าที่ย้ายมา USA แล้วยังไม่ได้ติด QR ใหม่</span>
                    : <span>* แสดงรายการทั้งหมดจาก SHOP และ USA รวมกัน</span>
                  }
                </div>

                <div className="text-center mt-3">
                  <a href="/" className="btn btn-link text-decoration-none">← กลับหน้าเมนู</a>
                </div>
              </div>
            </div>

            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 12, boxShadow: "0 0 20px rgba(0,0,0,0.5)", background: "#fff" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
